import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react'
import { Form, Formik } from 'formik'

import AudienceTargeting from '@/components/Form/AudienceTargeting'
import LoadingSpinner from '@/components/TailwindUI/LoadingSpinner'
import useFeatures from '@/components/useFeatures'
import { WORKSHOP_AGENTS } from '@/constants/featureToggles'
import cn from '@/utilities/cn'

import { appAudienceFormSchema } from './appAudienceFormSchema'
import AudienceIcon from './images/AudienceIcon'

const initialValues = {
  name: '',
  description: '',
  iconUrl: '',
  audience: []
}

type AppAudienceModalProps = {
  show: boolean
  onClose: () => void
  values?: any
  onSubmit: (values: Partial<typeof initialValues>) => void
}

export default function AppAudienceModal({ show, onClose, onSubmit, values }: AppAudienceModalProps) {
  return (
    <Transition show={show}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <TransitionChild
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </TransitionChild>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center text-center">
            <TransitionChild
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-0 scale-95"
              enterTo="opacity-100 translate-y-0 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 scale-100"
              leaveTo="opacity-0 translate-y-0 scale-95"
            >
              <DialogPanel className="relative my-8 w-full max-w-xl transform overflow-hidden rounded-lg bg-white px-6 pt-8 text-left shadow-xl transition-all">
                <AppAudienceForm
                  validationSchema={appAudienceFormSchema}
                  onClose={onClose}
                  values={values}
                  onSubmit={onSubmit}
                />
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

type AppAudienceFormProps = {
  isSubmitting?: boolean
  isImport?: boolean
  validationSchema: any
  onClose: () => void
  values?: any
  onSubmit: (values: Partial<typeof initialValues>) => void
}
export function AppAudienceForm({
  isSubmitting = false,
  validationSchema,
  onSubmit,
  onClose,
  values,
  isImport = false
}: AppAudienceFormProps) {
  const featureFlags = useFeatures()
  const agentsFlagOn = !!featureFlags[WORKSHOP_AGENTS]
  return (
    <Formik validationSchema={validationSchema} onSubmit={onSubmit} initialValues={values}>
      <Form className="flex max-h-full flex-1 flex-col justify-between divide-y divide-gray-200">
        <div className="overflow-auto pb-32">
          <div className="mb-4">
            <h3 className="inline-flex text-lg font-medium leading-6">
              <AudienceIcon className="m-2 scale-125" />
              <span className="m-2 font-semibold">Audience</span>
            </h3>
            <p className="mt-2 text-sm font-normal text-zinc-500">
              Audience targeting rules make this {agentsFlagOn ? 'agent' : 'app'} available to selected employees in
              their Work Assistant
            </p>
          </div>

          <div className="">
            <AudienceTargeting
              required={true}
              name="audience"
              title={''}
              startWithAudience={true}
              firstFilterMandatory={true}
            />
          </div>
        </div>

        <div className="flex items-center justify-end pb-6 pt-4">
          <div className="flex flex-row-reverse gap-3">
            <button
              type="submit"
              className={cn(
                'inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700'
              )}
              disabled={isSubmitting}
            >
              {isSubmitting ? <LoadingSpinner className="h6 w-6 text-blue-500" /> : isImport ? 'Finalize' : 'Save'}
            </button>
            {!isImport && (
              <button
                type="button"
                disabled={isSubmitting}
                className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50"
                onClick={onClose}
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      </Form>
    </Formik>
  )
}
